/* eslint-disable no-nested-ternary */
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Spinner } from '@components/index';
import useMemberDetails from '@features/group-management/components/v1/tabs/add-from-members-list-tab/useMemberDetails';
import { PaymentCollectionType } from '@features/group-management/constants';
import { AddFromMembersListItem } from '@features/group-management/types';
import MemberEmptyScreen from '@features/member-management/pages/members/components/MemberEmptyScreen';
import {
  FeeConfigurationType,
  MemberListType,
  PaymentCollectionArg
} from '@types';

import MemberListTable from './MemberListTable';
import MembersTabHeader from './MembersTabHeader';

interface AddFromMembersListTabProps {
  branchId: string;
  organizationId: string;
  groupAmount: string;
  groupDueDate: string;
  groupStartDate?: string;
  groupName?: string;
  isActiveGroup?: boolean;
  handleAddMembers: (members: MemberListType[]) => void;
  isFeeCategoryEnabled?: boolean;
  feeCategoryData?: FeeConfigurationType[];
  isQuickCollect?: boolean;
  groupPaymentCollectionType?: PaymentCollectionType;
  isImmediatePayment?: boolean;
  isCustomTerm?: boolean;
  groupFrequency?: number;
  selectedRows: string[];
  setSelectedRows: (data: string[]) => void;
  isAttendanceBasedFee?: boolean;
  isSessionBased?: boolean;
  sessionsPerCycle?: number;
}

const AddFromMembersListTab: FC<AddFromMembersListTabProps> = ({
  branchId,
  organizationId,
  groupAmount,
  groupDueDate,
  groupFrequency,
  groupStartDate,
  groupName,
  isActiveGroup = true,
  isFeeCategoryEnabled,
  feeCategoryData,
  handleAddMembers,
  isQuickCollect = false,
  groupPaymentCollectionType,
  isImmediatePayment = false,
  isCustomTerm = false,
  selectedRows,
  setSelectedRows,
  isAttendanceBasedFee = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isSessionBased = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  sessionsPerCycle
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [page, setPage] = useState<number>(1);

  const hasSearch = searchTerm?.trim()?.length > 0;

  const {
    memberMap,
    membersData,
    currentPageIds,
    totalCount,
    isLoading: isMemberListLoading,
    isSuccess: isMemberListSuccess,
    getAllMembers,
    getFeeCategoryDataFromConfig,
    updateMember
  } = useMemberDetails({
    branchId,
    organizationId,
    groupAmount,
    groupDueDate,
    groupName,
    feeCategoryData,
    searchTerm,
    page,
    isActiveGroup,
    setPage,
    groupPaymentCollectionType,
    groupFrequency,
    customFrequencyRequired: true
  });

  const [memberDetails, setMemberDetails] = useState<AddFromMembersListItem[]>(
    []
  );

  const onFeeCategoryAmountSave = (
    memberFeeConfig: FeeConfigurationType[],
    memberIndex: number
  ) => {
    setMemberDetails((prevMembers) =>
      prevMembers.map((member) =>
        member.id === memberDetails[memberIndex].id
          ? {
              ...member,
              feeCategoryData: getFeeCategoryDataFromConfig(memberFeeConfig),
              isManualChangeMadeForDueAmount: true,
              isAmountManuallyEdited: true
            }
          : member
      )
    );

    updateMember(memberDetails[memberIndex].id, {
      feeCategoryData: getFeeCategoryDataFromConfig(memberFeeConfig),
      isManualChangeMadeForDueAmount: true,
      isAmountManuallyEdited: true
    });
  };

  const onSessionsChange = (value: string, memberIndex: number) => {
    const num = value ? Number(value) : undefined;
    setMemberDetails((prevMembers) =>
      prevMembers.map((member) =>
        member.id === memberDetails[memberIndex].id
          ? {
              ...member,
              sessionsPerCycle: num,
              isManualChangeMadeForSessionsPerCycle: true
            }
          : member
      )
    );

    updateMember(memberDetails[memberIndex].id, {
      sessionsPerCycle: num,
      isManualChangeMadeForSessionsPerCycle: true
    });
  };

  const onDueAmountChange = (dueAmount: string, memberIndex: number) => {
    setMemberDetails((prevMembers) =>
      prevMembers.map((member) =>
        member.id === memberDetails[memberIndex].id
          ? {
              ...member,
              dueAmount,
              isManualChangeMadeForDueAmount: true,
              isAmountManuallyEdited: true
            }
          : member
      )
    );

    updateMember(memberDetails[memberIndex].id, {
      dueAmount,
      isManualChangeMadeForDueAmount: true,
      isAmountManuallyEdited: true
    });
  };

  const onFrequencyChange = (memberId: string, data: PaymentCollectionArg) => {
    setMemberDetails((prevMembers) =>
      prevMembers.map((member) =>
        member.id === memberId
          ? {
              ...member, // Keep all other properties the same
              paymentCollectionType: data.type,
              interval: data.interval,
              dueDate: data.date as string,
              isManualChangeMadeForDueDate: true
            }
          : member
      )
    );

    updateMember(memberId, {
      paymentCollectionType: data.type,
      interval: data.interval,
      dueDate: data.date as string,
      isManualChangeMadeForDueDate: true
    });
  };

  const onStartDateChange = (memberId: string, date?: Date) => {
    setMemberDetails((prevMembers) =>
      prevMembers.map((member) =>
        member.id === memberId
          ? {
              ...member,
              startDate: date?.toISOString(),
              isManualChangeMadeForStartDate: true
            }
          : member
      )
    );

    updateMember(memberId, {
      startDate: date?.toISOString(),
      isManualChangeMadeForStartDate: true
    });
  };

  const onSearch = useCallback((searchText: string) => {
    setSearchTerm(searchText);
    setPage(1); // Reset to first page when search changes
  }, []);

  const fomattedGrpCategory = feeCategoryData?.map((item) => ({
    id: item.branch_fee_category_id,
    label: item.category,
    value: item.amount,
    isManuallyEdited: false
  }));

  const formattedSelectedMembers: MemberListType[] = useMemo(() => {
    if (selectedRows.length > 0) {
      const selectedMembers = memberDetails.filter((member) =>
        selectedRows.includes(member.id)
      );

      const prevSelectedMembers = selectedRows.map((id) => memberMap.get(id));

      const allSelectedMembers = [...selectedMembers, ...prevSelectedMembers];

      const uniqueSelectedMembers = allSelectedMembers.filter(
        (member, index, self) =>
          index === self.findIndex((t) => t?.id === member?.id)
      );

      return uniqueSelectedMembers
        ? uniqueSelectedMembers.map((member) => ({
            id: member?.id || '',
            name: member?.name || '',
            primaryNumber: member?.mobile || '',
            primaryNumberCountryCode: member?.countryCode || '',
            amount: member?.dueAmount?.toString()?.trim() || '',
            dueDate: member?.dueDate || '',
            admissionNumber: member?.admissionNumber,
            paymentCollectionType: member?.paymentCollectionType || '',
            interval: member?.interval || '',
            feeCategoryData: member?.isAmountManuallyEdited
              ? member?.feeCategoryData || []
              : fomattedGrpCategory || [],
            startDate:
              member?.startDate || // eslint-disable-next-line no-nested-ternary
              (isCustomTerm
                ? groupStartDate || new Date().toISOString()
                : groupStartDate && new Date(groupStartDate) >= new Date()
                ? groupStartDate
                : new Date().toISOString()),
            sessionsPerCycle: member?.sessionsPerCycle
          }))
        : [];
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberDetails, selectedRows]);

  useEffect(() => {
    handleAddMembers(formattedSelectedMembers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(formattedSelectedMembers)]);

  useEffect(() => {
    getAllMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm]);

  useEffect(() => {
    setMemberDetails((prevMemberDetails) =>
      currentPageIds.map((id) => {
        // Check if the member is already in the previous state
        const existingMember = prevMemberDetails.find(
          (member) => member.id === id
        );

        // If found in previous memberDetails, return it; otherwise, get it from memberMap
        return existingMember || memberMap.get(id)!;
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(currentPageIds)]);

  useEffect(() => {
    if (memberDetails.length) {
      const updatedMemberDetails = memberDetails.map((member) => {
        member.isManualChangeMadeForDueDate =
          groupPaymentCollectionType !== PaymentCollectionType.ONCE &&
          member.isManualChangeMadeForDueDate;
        const updatedMember =
          !member.isManualChangeMadeForDueDate || isCustomTerm
            ? {
                ...member,
                dueDate: groupDueDate,
                paymentCollectionType: groupPaymentCollectionType,
                interval: groupFrequency?.toString() || '',
                ...(isSessionBased &&
                  sessionsPerCycle &&
                  !member.isManualChangeMadeForSessionsPerCycle && {
                    sessionsPerCycle
                  })
              }
            : member;

        return updatedMember;
      });

      if (
        JSON.stringify(updatedMemberDetails) !== JSON.stringify(memberDetails)
      ) {
        setMemberDetails(updatedMemberDetails);
      }
    }
  }, [
    groupDueDate,
    groupFrequency,
    groupPaymentCollectionType,
    isCustomTerm,
    memberDetails,
    isSessionBased,
    sessionsPerCycle
  ]);

  useEffect(() => {
    if (memberDetails.length) {
      const updatedMemberDetails = memberDetails.map((member) => {
        const updatedMember =
          !member.isManualChangeMadeForStartDate || isCustomTerm
            ? {
                ...member,
                startDate: groupStartDate || new Date().toString()
              }
            : member;
        return updatedMember;
      });
      if (
        JSON.stringify(updatedMemberDetails) !== JSON.stringify(memberDetails)
      ) {
        setMemberDetails(updatedMemberDetails);
      }
    }
  }, [groupStartDate, isCustomTerm, memberDetails]);

  useEffect(() => {
    if (memberDetails.length) {
      const updatedMemberDetails = memberDetails.map((member) => {
        const updatedMember =
          !member.isManualChangeMadeForDueAmount || isCustomTerm
            ? {
                ...member,
                dueAmount: groupAmount
              }
            : member;
        return updatedMember;
      });
      if (
        JSON.stringify(updatedMemberDetails) !== JSON.stringify(memberDetails)
      ) {
        setMemberDetails(updatedMemberDetails);
      }
    }
  }, [groupAmount, isCustomTerm, memberDetails]);

  useEffect(() => {
    if (memberDetails.length) {
      const updatedMemberDetails = memberDetails.map((member) => {
        const updatedMember = !member.isManualChangeMadeForSessionsPerCycle
          ? {
              ...member,
              sessionsPerCycle
            }
          : member;
        return updatedMember;
      });
      if (
        JSON.stringify(updatedMemberDetails) !== JSON.stringify(memberDetails)
      ) {
        setMemberDetails(updatedMemberDetails);
      }
    }
  }, [sessionsPerCycle, memberDetails]);

  if (isMemberListLoading) {
    return (
      <div className="flex h-150 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg bg-white p-6  ${
        membersData?.pagination?.total_rows === 0 && !isMemberListLoading
          ? ''
          : 'shadow-tableShadow'
      }`}
    >
      {(searchTerm !== '' || totalCount > 0) && (
        <MembersTabHeader
          totalCount={totalCount}
          searchTerm={searchTerm}
          selectedCount={selectedRows.length}
          onSearch={onSearch}
        />
      )}
      {membersData?.pagination?.total_rows === 0 && !isMemberListLoading ? (
        <div className="h-90">
          <MemberEmptyScreen hasSearch={hasSearch} />
        </div>
      ) : isMemberListSuccess && memberDetails && memberDetails.length > 0 ? (
        <MemberListTable
          onFeeCategoryAmountSave={onFeeCategoryAmountSave}
          onAmountChange={onDueAmountChange}
          totalCount={totalCount}
          selectedRows={selectedRows}
          memberDetails={memberDetails}
          isFeeCategoryEnabled={isFeeCategoryEnabled}
          feeCategoryData={feeCategoryData}
          page={page}
          handleStartDateChange={onStartDateChange}
          setSelectedRows={setSelectedRows}
          setPage={setPage}
          isQuickCollect={isQuickCollect}
          groupPaymentCollectionType={groupPaymentCollectionType}
          isImmediatePayment={isImmediatePayment}
          isCustomTerm={isCustomTerm}
          groupDueDate={groupDueDate}
          groupFrequency={groupFrequency}
          handleFrequencyChange={onFrequencyChange}
          showFrequencySelector
          isAttendanceBasedFee={isAttendanceBasedFee}
          isSessionBased={isSessionBased}
          sessionsPerCycle={sessionsPerCycle}
          handleSessionsChange={onSessionsChange}
        />
      ) : (
        <div className="flex h-150 items-center justify-center">
          <Spinner />
        </div>
      )}
    </div>
  );
};

export default AddFromMembersListTab;
